import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading", payment_status: null });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", message: "Missing session id" }); return; }
    let cancelled = false;
    const poll = async (n) => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") setState({ status: "paid", ...data });
        else if (data.status === "expired") setState({ status: "expired", ...data });
        else if (n < 8) { setTimeout(() => { setAttempts(n + 1); poll(n + 1); }, 1800); }
        else setState({ status: "pending", ...data });
      } catch {
        setState({ status: "error", message: "Could not verify payment." });
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  const isPaid = state.status === "paid";
  const failed = state.status === "expired" || state.status === "error";

  return (
    <PublicLayout>
      <section className="min-h-[60vh] flex items-center justify-center px-6 py-20 bg-[var(--parchment)]">
        <div className="max-w-md text-center" data-testid="checkout-status">
          {state.status === "loading" || state.status === "pending" ? (
            <>
              <Loader2 size={32} className="mx-auto animate-spin text-[var(--ink)]" />
              <h1 className="font-display font-black text-3xl mt-6">Confirming payment…</h1>
              <p className="mt-3 text-[var(--ink-soft)]">Attempt {attempts + 1} of 8.</p>
            </>
          ) : isPaid ? (
            <>
              <CheckCircle2 size={40} className="mx-auto text-[var(--viridian)]" />
              <div className="eyebrow mt-6 text-[var(--viridian)]">Payment received</div>
              <h1 className="font-display font-black text-4xl mt-3" data-testid="checkout-success">Welcome to Luminary.</h1>
              <p className="mt-3 text-[var(--ink-soft)]">Your account has been upgraded.</p>
              <div className="mt-8 flex justify-center gap-3">
                <Link to="/dashboard" className="btn-primary">Go to dashboard →</Link>
                <Link to="/luminous" className="btn-secondary">Try Luminous AI</Link>
              </div>
            </>
          ) : failed ? (
            <>
              <XCircle size={40} className="mx-auto text-red-700" />
              <h1 className="font-display font-black text-3xl mt-6">Payment not completed.</h1>
              <p className="mt-3 text-[var(--ink-soft)]">{state.message || "Please try again."}</p>
              <Link to="/pricing" className="btn-primary mt-6 inline-flex">Back to pricing</Link>
            </>
          ) : null}
        </div>
      </section>
    </PublicLayout>
  );
}
